/**
 * EmployerBillingPanel.jsx — F.4.4 Employer Billing UI.
 *
 * Canonical home for employer plan + billing + usage state. Mounted
 * EXCLUSIVELY on /employer/account. MUST NOT be mounted on
 * /employer/organisation (that surface is candidate-visible).
 *
 * Data: GET /api/employer/v1/billing/subscription (additive,
 * already-live as of F.4.3 Batch B). Backend strips Stripe IDs from
 * the response; the UI MUST NOT add them back.
 *
 * Doctrine:
 *   • Plan selector + Manage Billing CTAs are visible when
 *     feature_flag === 'enabled'. In 'disabled' and 'shadow' mode
 *     they render but are disabled with explanatory copy — operator
 *     can flip the flag without redeploying the UI.
 *   • Non-owner members see read-only state — no mutation CTAs.
 *   • Lifecycle banner renders for past_due / downgraded_to_starter /
 *     cancelled / expired. 'active' is the no-banner happy state.
 *   • Usage bars use the entitlement caps already present in
 *     emp_plan_entitlements (active_roles_cap, eval_cap_monthly, etc).
 *     For F.4.4 the bars are rendered from the existing /v1/me
 *     entitlement payload — no new endpoint.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, TriangleAlert, ExternalLink, ShieldCheck,
  CheckCircle2, History, Lock,
} from 'lucide-react';
import { toast } from 'sonner';

import { apiFetch } from '@/lib/api';
import Disclaimer from '@/components/Disclaimer';

// ─────────────────────────────────────────────────────────────────
// F.4.4.DOCTRINE-CORRECTION (2026-02-26):
// Plan catalogue is now read from the backend (server-side projection
// of `emp_plan_catalogue`), delivered via
// /api/employer/v1/billing/subscription → `catalogue: [...]`.
// NOTHING about plan caps, pricing, currency, role-activation cap,
// or plan label/blurb is hardcoded in this file. When the operator
// updates emp_plan_catalogue rows (or their `notes` prose), this UI
// follows on the next page load — no redeploy.
//
// The ONLY frontend-resident copy below is:
//   - blurb / label one-liners (presentation strings)
//   - the bullet TEMPLATE strings (how to render each cap as a row)
// Both are pure presentation; the NUMBERS themselves all come from
// the catalogue payload.
// ─────────────────────────────────────────────────────────────────
const TIER_PRESENTATION = {
  starter: {
    label: 'Starter',
    blurb: 'Free baseline for small teams getting started.',
  },
  professional: {
    label: 'Professional',
    blurb: 'For active recruiting teams.',
    highlight: true,
  },
  business: {
    label: 'Business',
    blurb: 'For scale-stage hiring teams.',
  },
  enterprise: {
    label: 'Enterprise',
    blurb: 'Admin-provisioned. No self-serve activation.',
  },
};

// Pure presentation: format a cent value into "£N / mo" or "£N / yr".
function fmtPriceFromCents(cents, currency, period) {
  if (cents === null || cents === undefined) return null;
  const symbol = currency === 'GBP' ? '£'
               : currency === 'USD' ? '$'
               : currency === 'EUR' ? '€'
               : `${currency} `;
  const value = Math.round(cents / 100).toLocaleString();
  return `${symbol}${value} / ${period === 'annual' ? 'yr' : 'mo'}`;
}

// Pure presentation: build the bullet list for a tier from its
// catalogue payload. NUMBERS come from the payload; only the
// presentation templates ("X active roles", "Y evaluations / month",
// etc.) live here.
function buildBullets(plan) {
  if (plan.is_contact_only) {
    return [
      'Custom / unlimited active roles',
      'Custom / unlimited evaluations',
      'Custom / unlimited governance packs',
      'Custom / unlimited signals',
      'Dedicated reviewer pool & SLAs',
    ];
  }
  const out = [];
  if (plan.active_roles_cap !== null && plan.active_roles_cap !== undefined) {
    out.push(`${plan.active_roles_cap} active role${plan.active_roles_cap === 1 ? '' : 's'}`);
  }
  if (plan.eval_cap_monthly !== null && plan.eval_cap_monthly !== undefined) {
    out.push(`${plan.eval_cap_monthly} evaluation${plan.eval_cap_monthly === 1 ? '' : 's'} / month`);
  }
  if (plan.governance_pack_allowance_monthly !== null && plan.governance_pack_allowance_monthly !== undefined) {
    const n = plan.governance_pack_allowance_monthly;
    out.push(`${n} governance pack${n === 1 ? '' : 's'} / month`);
  }
  if (plan.signals_allowance_monthly !== null && plan.signals_allowance_monthly !== undefined) {
    const n = plan.signals_allowance_monthly;
    out.push(`${n} signal${n === 1 ? '' : 's'} / month`);
  }
  if (plan.role_activation_cap_monthly !== null && plan.role_activation_cap_monthly !== undefined) {
    const n = plan.role_activation_cap_monthly;
    out.push(`${n} role activation${n === 1 ? '' : 's'} / month`);
  }
  // Starter is the only tier with "no overage (hard stop)"; this is
  // a doctrine-locked property of the free tier and cannot be
  // expressed by a cap value. We surface it for Starter only.
  if (plan.plan_tier === 'starter') {
    out.push('No overage (hard stop at cap)');
  }
  return out;
}

const PLAN_TIER_LABEL = {
  starter:      'Starter',
  professional: 'Professional',
  business:     'Business',
  enterprise:   'Enterprise',
};

const LIFECYCLE_LABEL = {
  active:                'Active',
  past_due:              'Past due',
  downgraded_to_starter: 'Downgraded to Starter',
  cancelled:             'Cancelled',
  expired:               'Expired',
};


function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return null;
  }
}


// ─────────────────────────────────────────────────────────────────
// Lifecycle banner — 5-state employer doctrine.
// ─────────────────────────────────────────────────────────────────
function LifecycleBanner({ lifecycle, ent, onManageBilling }) {
  if (!lifecycle || lifecycle === 'active') return null;
  const past = fmtDate(ent?.past_due_since);
  const down = fmtDate(ent?.downgraded_to_starter_at);
  const accessTo = fmtDate(ent?.access_expires_at);
  const variants = {
    past_due: {
      tone:    'amber',
      title:   'Your last payment failed.',
      body:    past
        ? `Please update your payment method to keep your plan active. Past due since ${past}.`
        : 'Please update your payment method to keep your plan active.',
      cta:     'Update payment method',
    },
    downgraded_to_starter: {
      tone:    'rose',
      title:   'Your organisation has been downgraded to Starter.',
      body:    down
        ? `The previous plan was paused on ${down} after a grace period. Higher-tier caps no longer apply.`
        : 'The previous plan was paused after a grace period. Higher-tier caps no longer apply.',
      cta:     'Restore previous plan',
    },
    cancelled: {
      tone:    'rose',
      title:   'Your subscription has been cancelled.',
      body:    accessTo
        ? `Access to higher-tier features ends ${accessTo}. Re-subscribe to restore caps.`
        : 'Re-subscribe to restore higher-tier caps.',
      cta:     'Choose a plan',
    },
    expired: {
      tone:    'rose',
      title:   'Your subscription has expired.',
      body:    'Re-subscribe to restore the previous plan tier.',
      cta:     'Choose a plan',
    },
  };
  const v = variants[lifecycle];
  if (!v) return null;
  const toneCls = v.tone === 'amber'
    ? 'border-amber-500/30 bg-amber-500/10 text-amber-900'
    : 'border-rose-500/30 bg-rose-500/10 text-rose-900';
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-start gap-3 rounded-md border ${toneCls} p-3 text-sm`}
      data-testid={`emp-billing-lifecycle-${lifecycle}-banner`}
      role="status"
      aria-live="polite"
    >
      <TriangleAlert className="h-4 w-4 shrink-0 mt-[2px]" aria-hidden="true" />
      <div className="flex-1 space-y-2">
        <p className="font-medium">{v.title}</p>
        <p className="text-xs opacity-90">{v.body}</p>
        <Button
          size="sm"
          variant="outline"
          onClick={onManageBilling}
          className="border-current bg-transparent hover:bg-current/10"
          data-testid={`emp-billing-lifecycle-${lifecycle}-cta`}
        >
          {v.cta}
        </Button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Usage bars — SURFACE 2 doctrine (F.4.4.DOCTRINE-CORRECTION):
// CONSUMPTION semantics. Headline reads "<used> / <cap>"; the bar
// fill is the consumption ratio. Fractions belong HERE — not on the
// top strip (which uses plain-language remaining pills).
// ─────────────────────────────────────────────────────────────────
function UsageBar({ label, used, cap, testId }) {
  const safeUsed = Number.isFinite(used) ? Math.max(0, used) : 0;
  const safeCap  = Number.isFinite(cap) && cap > 0 ? cap : null;
  const pct = safeCap ? Math.min(100, Math.round((safeUsed / safeCap) * 100)) : 0;
  const isDanger = safeCap !== null && safeUsed >= safeCap;
  const isWarn   = safeCap !== null && !isDanger && safeUsed / safeCap >= 0.8;
  const barCls = isDanger
    ? 'bg-rose-500'
    : isWarn ? 'bg-amber-500' : 'bg-ndor-ink';
  // Headline framing: "<used> / <cap>" (catalogue "unlimited" maps
  // to the word "unlimited" for the denominator — not "∞" — per the
  // top-strip plain-language doctrine).
  const capDisplay = safeCap === null ? 'unlimited' : String(safeCap);
  return (
    <div data-testid={testId} className="space-y-2">
      <div className="flex justify-between items-baseline gap-2">
        <span className="text-xs text-ndor-ink-muted">{label}</span>
        <span className="text-xs tabular-nums text-ndor-ink font-medium">
          {safeUsed} <span className="text-ndor-ink-muted font-normal">/ {capDisplay}</span>
        </span>
      </div>
      <div
        className="h-2 rounded-full bg-ndor-edge overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={safeCap || 100}
        aria-valuenow={safeUsed}
        aria-label={`${label}: ${safeUsed} of ${capDisplay} used`}
      >
        <div
          className={`h-full ${barCls} transition-all`}
          style={{ width: `${safeCap ? pct : 0}%` }}
        />
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Plan selector cards + Monthly/Annual toggle.
// Source of truth: `catalogue` prop (server-side projection of
// `emp_plan_catalogue`). No frontend constants for caps, prices or
// currency — only presentation helpers above.
// ─────────────────────────────────────────────────────────────────
function PlanSelector({
  catalogue, currentTier, currentPeriod, period, onPeriodChange,
  canMutate, onChoosePlan, disabledReason,
}) {
  if (!catalogue || catalogue.length === 0) {
    return (
      <div
        className="text-xs text-ndor-ink-muted"
        data-testid="emp-billing-plan-selector-empty"
      >
        Plan catalogue is loading…
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="emp-billing-plan-selector">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-ndor-ink">Choose a plan</h3>
        <div
          className="inline-flex rounded-md border border-ndor-edge bg-ndor-surface-alt overflow-hidden text-xs"
          role="tablist"
          aria-label="Billing period"
        >
          {['monthly', 'annual'].map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={period === p}
              onClick={() => onPeriodChange(p)}
              data-testid={`emp-billing-period-${p}`}
              className={
                period === p
                  ? 'px-3 py-1.5 bg-ndor-ink text-ndor-surface'
                  : 'px-3 py-1.5 text-ndor-ink-muted hover:text-ndor-ink'
              }
            >
              {p === 'monthly' ? 'Monthly' : 'Annual · save ~17%'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
        {catalogue.map((plan) => {
          const presentation = TIER_PRESENTATION[plan.plan_tier] || {
            label: plan.plan_tier, blurb: '',
          };
          const isContactOnly = !!plan.is_contact_only;
          const isCurrent = currentTier === plan.plan_tier
            && (plan.plan_tier === 'starter'
                || isContactOnly
                || !currentPeriod
                || currentPeriod === period);
          // Pricing from the catalogue payload (already parsed
          // server-side). Contact-only tiers and tiers missing a
          // price string both fall through to "Contact sales".
          const cents = period === 'annual'
            ? plan.price_annual_cents
            : plan.price_monthly_cents;
          const price = isContactOnly || cents === null || cents === undefined
            ? 'Contact sales'
            : fmtPriceFromCents(cents, plan.currency, period);
          const bullets = buildBullets(plan);
          return (
            <Card
              key={plan.plan_tier}
              className={`flex flex-col h-full border ${
                isCurrent
                  ? 'border-ndor-ink ring-1 ring-ndor-ink/30'
                  : presentation.highlight
                    ? 'border-ndor-edge ring-1 ring-amber-300/40'
                    : 'border-ndor-edge'
              } shadow-sm`}
              data-testid={`emp-billing-plan-card-${plan.plan_tier}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-ndor-ink">
                    {presentation.label}
                  </CardTitle>
                  {isCurrent && (
                    <Badge
                      variant="outline"
                      data-testid={`emp-billing-plan-current-badge-${plan.plan_tier}`}
                    >
                      Current
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-ndor-ink-muted">{presentation.blurb}</p>
              </CardHeader>
              <CardContent className="pt-0 flex flex-col flex-1 gap-3">
                <p
                  className="text-2xl font-serif-display font-semibold text-ndor-ink tabular-nums"
                  data-testid={`emp-billing-plan-price-${plan.plan_tier}`}
                >
                  {price}
                </p>
                <ul className="text-xs text-ndor-ink-secondary space-y-1.5 flex-1">
                  {bullets.map((b) => (
                    <li key={b} className="flex items-start gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-ndor-ink-muted" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {isContactOnly ? (
                  <p
                    className="w-full mt-auto text-xs text-ndor-ink-muted italic px-1 py-2 border-t border-ndor-edge"
                    data-testid={`emp-billing-plan-contact-note-${plan.plan_tier}`}
                  >
                    Admin-provisioned. Contact sales for activation.
                  </p>
                ) : (
                  <Button
                    variant={isCurrent ? 'outline' : 'default'}
                    size="sm"
                    disabled={!canMutate || isCurrent || plan.plan_tier === 'starter'}
                    onClick={() => onChoosePlan(plan.plan_tier, period)}
                    data-testid={`emp-billing-plan-choose-${plan.plan_tier}`}
                    className="w-full mt-auto"
                    title={!canMutate ? disabledReason : undefined}
                  >
                    {isCurrent ? 'Current plan' :
                     plan.plan_tier === 'starter' ? 'Starter is default' :
                     'Choose plan'}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Main panel.
// ─────────────────────────────────────────────────────────────────
export default function EmployerBillingPanel({ accountId }) {
  const [sub, setSub]           = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [period, setPeriod]     = useState('monthly');
  const [acting, setActing]     = useState(false);

  // F.4.4.DOCTRINE-CORRECTION (2026-02-26):
  // Single data source — /v1/billing/subscription now exposes the
  // entitlement caps + period-to-date counters (non-Stripe fields
  // only). The previous /v1/plan side-fetch caused the billing
  // panel to render "0 / unlimited" while the dashboard strip
  // showed real caps. Reading entitlement directly from `sub`
  // guarantees both surfaces show identical numbers.
  const ent = sub?.entitlement || null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch('/api/employer/v1/billing/subscription');
      setSub(r);
    } catch (err) {
      setError(err?.message || 'Failed to load billing.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onChoosePlan = useCallback(async (tier, billing_period) => {
    if (!sub?.is_owner) {
      toast.error('Only the organisation owner can manage billing.');
      return;
    }
    if (sub?.feature_flag !== 'enabled') {
      toast.message('Employer billing is not active yet.');
      return;
    }
    setActing(true);
    try {
      const r = await apiFetch('/api/employer/v1/billing/checkout-session', {
        method: 'POST',
        body:   JSON.stringify({ plan_tier: tier, billing_period }),
      });
      if (!r?.checkout_url) throw new Error('No checkout URL returned.');
      window.location.assign(r.checkout_url);
    } catch (err) {
      toast.error(err?.message || 'Unable to start checkout.');
      setActing(false);
    }
  }, [sub]);

  const onManageBilling = useCallback(async () => {
    if (!sub?.is_owner) {
      toast.error('Only the organisation owner can manage billing.');
      return;
    }
    if (sub?.feature_flag !== 'enabled') {
      toast.message('Employer billing is not active yet.');
      return;
    }
    setActing(true);
    try {
      const r = await apiFetch('/api/employer/v1/billing/customer-portal', {
        method: 'POST',
        body:   JSON.stringify({}),
      });
      if (!r?.portal_url) throw new Error('No portal URL returned.');
      window.location.assign(r.portal_url);
    } catch (err) {
      toast.error(err?.message || 'Unable to open billing portal.');
      setActing(false);
    }
  }, [sub]);

  if (loading) {
    return (
      <Card className="border-ndor-edge shadow-sm" data-testid="emp-billing-loading">
        <CardContent className="py-8 flex items-center justify-center gap-2 text-sm text-ndor-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading billing…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="border-ndor-edge shadow-sm" data-testid="emp-billing-error">
        <CardContent className="py-6 text-sm text-ndor-ink-muted">
          {error}
        </CardContent>
      </Card>
    );
  }
  if (!sub) return null;

  const flag       = sub.feature_flag || 'disabled';
  const lifecycle  = sub.entitlement?.lifecycle_status || 'active';
  const planTier   = sub.entitlement?.plan_tier || 'starter';
  const planPeriod = sub.entitlement?.billing_period || null;
  const canMutate  = sub.is_owner && flag === 'enabled';
  const disabledReason = !sub.is_owner
    ? 'Only the organisation owner can manage billing.'
    : flag !== 'enabled'
      ? 'Employer billing is not active yet.'
      : undefined;
  const renews = fmtDate(sub.billing_link?.current_period_end);
  const cancelAtPeriodEnd = !!sub.billing_link?.cancel_at_period_end;

  return (
    <div className="space-y-4" data-testid="emp-billing-panel">
      {/* ── Current subscription state card ──────────────────── */}
      <Card className="border-ndor-edge shadow-sm" data-testid="emp-billing-state-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-ndor-ink-muted" />
            Subscription
          </CardTitle>
          <Badge
            variant="outline"
            data-testid="emp-billing-lifecycle-badge"
            className={
              lifecycle === 'active' ? 'border-emerald-300/60 text-emerald-700'
              : lifecycle === 'past_due' ? 'border-amber-400/60 text-amber-700'
              : 'border-rose-400/60 text-rose-700'
            }
          >
            {LIFECYCLE_LABEL[lifecycle] || lifecycle}
          </Badge>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <span
              className="text-2xl font-serif-display font-semibold text-ndor-ink"
              data-testid="emp-billing-plan-tier"
            >
              {PLAN_TIER_LABEL[planTier] || planTier}
            </span>
            {planPeriod && (
              <Badge
                variant="outline"
                className="border-ndor-edge text-ndor-ink-muted"
                data-testid="emp-billing-plan-period"
              >
                {planPeriod === 'annual' ? 'Annual' : 'Monthly'}
              </Badge>
            )}
            {cancelAtPeriodEnd && (
              <Badge
                variant="outline"
                className="border-rose-400/40 text-rose-700"
                data-testid="emp-billing-cancel-at-period-end"
              >
                Cancels at period end
              </Badge>
            )}
          </div>
          {renews && (
            <p className="text-xs text-ndor-ink-muted" data-testid="emp-billing-renews">
              {cancelAtPeriodEnd ? 'Access ends ' : 'Renews '}{renews}
            </p>
          )}
          {flag !== 'enabled' && (
            <div
              className="mt-2 flex items-start gap-2 rounded-md border border-ndor-edge bg-ndor-surface-alt/40 p-2 text-[11px] text-ndor-ink-muted"
              data-testid="emp-billing-flag-disabled-note"
            >
              <Lock className="h-3 w-3 mt-0.5 shrink-0" />
              <span>
                <strong className="text-ndor-ink">Employer billing is not active yet.</strong>
                {' '}You can preview plans and caps below; checkout activates after operator review.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Lifecycle banner (only when non-active) ──────────── */}
      <LifecycleBanner
        lifecycle={lifecycle}
        ent={sub.entitlement}
        onManageBilling={onManageBilling}
      />

      {/* ── Usage / cap visualisation ───────────────────────── */}
      <Card className="border-ndor-edge shadow-sm" data-testid="emp-billing-usage-card">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-ndor-ink">
            Current period usage
          </CardTitle>
          <p className="text-xs text-ndor-ink-muted">
            Consumption against your plan caps for this billing
            period.
          </p>
        </CardHeader>
        <CardContent className="pt-0 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 pb-5">
          <UsageBar
            label="Roles used"
            used={ent?.active_roles_current ?? 0}
            cap={ent?.active_roles_cap ?? null}
            testId="emp-billing-usage-roles"
          />
          <UsageBar
            label="Evaluations used"
            used={ent?.evaluations_used_this_period ?? 0}
            cap={ent?.eval_cap_monthly ?? null}
            testId="emp-billing-usage-evaluations"
          />
          <UsageBar
            label="Governance packs used"
            used={ent?.governance_packs_used_this_period ?? 0}
            cap={ent?.governance_pack_allowance_monthly ?? null}
            testId="emp-billing-usage-packs"
          />
          <UsageBar
            label="Signals used this month"
            used={ent?.signals_used_this_period ?? 0}
            cap={ent?.signals_allowance_monthly ?? null}
            testId="emp-billing-usage-signals"
          />
        </CardContent>
      </Card>

      {/* ── Plan selector ────────────────────────────────────── */}
      <Card className="border-ndor-edge shadow-sm">
        <CardContent className="pt-5 space-y-4">
          <PlanSelector
            catalogue={sub.catalogue}
            currentTier={planTier}
            currentPeriod={planPeriod}
            period={period}
            onPeriodChange={setPeriod}
            canMutate={canMutate && !acting}
            onChoosePlan={onChoosePlan}
            disabledReason={disabledReason}
          />
        </CardContent>
      </Card>

      {/* ── Actions row ──────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <Button
          variant="outline"
          onClick={onManageBilling}
          disabled={!canMutate || acting || !sub.billing_link?.has_customer}
          data-testid="emp-billing-manage-cta"
          title={
            !sub.is_owner
              ? 'Only the organisation owner can manage billing.'
              : flag !== 'enabled'
                ? 'Employer billing is not active yet.'
                : !sub.billing_link?.has_customer
                  ? 'No active billing for this organisation yet.'
                  : undefined
          }
        >
          {acting
            ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            : <ExternalLink className="h-4 w-4 mr-1.5" />}
          Manage billing
        </Button>
        <Button
          variant="ghost"
          disabled
          data-testid="emp-billing-history-cta"
          className="text-ndor-ink-muted"
          title="Invoice history available after activation."
        >
          <History className="h-4 w-4 mr-1.5" />
          View invoice history
        </Button>
      </div>

      {/* ── Placeholder invoice/history section ─────────────── */}
      <Card className="border-ndor-edge shadow-sm" data-testid="emp-billing-invoices-placeholder">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-ndor-ink">
            Invoices
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-xs text-ndor-ink-muted">
            Invoice history will appear here once your subscription is active.
            Charges are processed by Stripe; receipts are emailed to the
            billing owner.
          </p>
        </CardContent>
      </Card>

      {/* RC 1.61 S2.B.1 — institutional disclaimer (Employer Billing) */}
      <Disclaimer variant="block" testId="employer-billing-disclaimer" />
    </div>
  );
}
